import { PageViewEventName } from "@/types/event-types";
import {
  AnyContext,
  AnyRoute,
  createRoute as createRouteTanstack,
  ResolveFullPath,
  ResolveId,
  ResolveParams,
  Route,
  RouteConstraints,
  RouteOptions,
} from "@tanstack/react-router";

type PageStaticData = {
  title: string;
  eventName: PageViewEventName;
};

/**
 * Wrapper around `createRoute` that requires page static data.
 * @param options.staticData.title - The title of the page.
 * @param options.staticData.eventName - The event name for the page view track event, must be in the format of `snake_case_page_viewed`.
 */
export const createPageRoute = <
  TRegister = unknown,
  TParentRoute extends RouteConstraints["TParentRoute"] = AnyRoute,
  TPath extends RouteConstraints["TPath"] = "/",
  TFullPath extends RouteConstraints["TFullPath"] = ResolveFullPath<
    TParentRoute,
    TPath
  >,
  TCustomId extends RouteConstraints["TCustomId"] = string,
  TId extends RouteConstraints["TId"] = ResolveId<
    TParentRoute,
    TCustomId,
    TPath
  >,
  TSearchValidator = undefined,
  TParams = ResolveParams<TPath>,
  TRouteContextFn = AnyContext,
  TBeforeLoadFn = AnyContext,
  TLoaderDeps extends Record<string, unknown> = {},
  TLoaderFn = undefined,
  TChildren = unknown,
  TSSR = unknown,
  const TServerMiddlewares = unknown,
>(
  options: RouteOptions<
    TRegister,
    TParentRoute,
    TId,
    TCustomId,
    TFullPath,
    TPath,
    TSearchValidator,
    TParams,
    TLoaderDeps,
    TLoaderFn,
    AnyContext,
    TRouteContextFn,
    TBeforeLoadFn,
    TSSR,
    TServerMiddlewares
  > & {
    staticData: PageStaticData;
  },
): Route<
  TRegister,
  TParentRoute,
  TPath,
  TFullPath,
  TCustomId,
  TId,
  TSearchValidator,
  TParams,
  AnyContext,
  TRouteContextFn,
  TBeforeLoadFn,
  TLoaderDeps,
  TLoaderFn,
  TChildren,
  TSSR,
  TServerMiddlewares
> => createRouteTanstack(options);
